import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { databaseService } from '../services/database';
import { createPCAgentClient } from '../services/pcAgent';
import {
  Configuration,
  ConfigurationRelease,
  Experience,
  Room,
  Network,
  PCInstance,
  PCSyncStatus,
} from '../types/database';
import ConfigurationMatrix from '../components/deployment/ConfigurationMatrix';
import RoomSyncStatus from '../components/deployment/RoomSyncStatus';
import AssignReleaseDialog from '../components/deployment/AssignReleaseDialog';
import PromoteReleaseDialog from '../components/deployment/PromoteReleaseDialog';

interface Toast {
  message: string;
  type: 'success' | 'error';
  id: number;
}

interface DeploymentViewProps {
  isAdmin: boolean;
}

export default function DeploymentView({ isAdmin }: DeploymentViewProps) {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [configReleases, setConfigReleases] = useState<ConfigurationRelease[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [allRooms, setAllRooms] = useState<Room[]>([]);

  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [pcInstances, setPcInstances] = useState<PCInstance[]>([]);
  const [syncStatuses, setSyncStatuses] = useState<PCSyncStatus[]>([]);

  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  // Dialog state
  const [assignDialog, setAssignDialog] = useState<{
    isOpen: boolean;
    configId?: string;
    phase?: string;
    existingRelease?: ConfigurationRelease;
  }>({ isOpen: false });

  const [promoteDialog, setPromoteDialog] = useState<{
    isOpen: boolean;
    release?: ConfigurationRelease;
    existingPhases: string[];
  }>({ isOpen: false, existingPhases: [] });

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { message, type, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  // Load initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    const [configs, releases, exps, rooms] = await Promise.all([
      databaseService.listConfigurations(),
      databaseService.getAllConfigurationReleases(),
      databaseService.listExperiences(),
      databaseService.listRooms(),
    ]);
    setConfigurations(configs);
    setConfigReleases(releases);
    setExperiences(exps);
    setAllRooms(rooms);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load room/PC data when config is selected
  useEffect(() => {
    if (!selectedConfigId) {
      setRooms([]);
      setNetworks([]);
      setPcInstances([]);
      setSyncStatuses([]);
      return;
    }

    let cancelled = false;

    async function loadConfigDetails() {
      const configRooms = await databaseService.getRoomsForConfiguration(selectedConfigId!);
      if (cancelled) return;
      setRooms(configRooms);

      const allNetworks: Network[] = [];
      const allPCs: PCInstance[] = [];
      for (const room of configRooms) {
        const nets = await databaseService.getNetworksInRoom(room.id);
        allNetworks.push(...nets);
        for (const net of nets) {
          const pcs = await databaseService.getPCInstancesInNetwork(net.id);
          allPCs.push(...pcs);
        }
      }
      if (cancelled) return;
      setNetworks(allNetworks);
      setPcInstances(allPCs);

      const pcIds = allPCs.map((pc) => pc.id);
      if (pcIds.length > 0) {
        const statuses = await databaseService.getSyncStatusByPCIds(pcIds);
        if (!cancelled) setSyncStatuses(statuses);
      }
    }

    loadConfigDetails();
    return () => { cancelled = true; };
  }, [selectedConfigId]);

  // Realtime subscription for sync status updates
  useEffect(() => {
    const channel = supabase
      .channel('deployment-sync-status')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'infra',
          table: 'pc_sync_status',
        },
        (payload) => {
          const updated = payload.new as PCSyncStatus;
          if (!updated?.id) return;

          setSyncStatuses((prev) => {
            const idx = prev.findIndex((s) => s.id === updated.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = updated;
              return next;
            }
            return [...prev, updated];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Handlers
  const handleAssignRelease = (configId: string, phase: string, existingRelease?: ConfigurationRelease) => {
    setAssignDialog({ isOpen: true, configId, phase, existingRelease });
  };

  const handlePromoteRelease = (release: ConfigurationRelease, existingPhases: string[]) => {
    setPromoteDialog({ isOpen: true, release, existingPhases });
  };

  const handleDeleteRelease = async (releaseId: string) => {
    const success = await databaseService.deleteConfigurationRelease(releaseId);
    if (success) {
      showToast('Release assignment removed', 'success');
      loadData();
    } else {
      showToast('Failed to delete release assignment', 'error');
    }
  };

  const handleDialogSave = () => {
    loadData();
    showToast('Release assignment saved', 'success');
  };

  const handlePromoteSave = () => {
    loadData();
    showToast('Release promoted', 'success');
  };

  const triggerPCUpdate = async (pcInstanceId: string) => {
    const pc = pcInstances.find((p) => p.id === pcInstanceId);
    if (!pc) return;

    // Set should_update flag
    await databaseService.setShouldUpdate([pcInstanceId], true);

    // Call agent API if online
    const ip = pc.ip_address || pc.tailscale_ip;
    if (ip && pc.status === 'online') {
      const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
      const result = await client.triggerExperienceUpdate();
      if (result.success) {
        showToast(`Update triggered on ${pc.pc_name || pc.id.slice(0, 8)}`, 'success');
      } else {
        showToast(`Flag set, but API call failed on ${pc.pc_name || pc.id.slice(0, 8)}: ${result.error}`, 'error');
      }
    } else {
      showToast(`should_update set on ${pc.pc_name || pc.id.slice(0, 8)} (offline — will sync on next boot)`, 'success');
    }
  };

  const triggerRoomUpdate = async (_roomId: string, pcIds: string[]) => {
    // Set should_update on all PCs
    await databaseService.setShouldUpdate(pcIds, true);

    // Trigger API on all online PCs
    const onlinePCs = pcInstances.filter(
      (pc) => pcIds.includes(pc.id) && pc.status === 'online' && (pc.ip_address || pc.tailscale_ip)
    );

    const results = await Promise.allSettled(
      onlinePCs.map(async (pc) => {
        const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
        return client.triggerExperienceUpdate();
      })
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled' && (r as PromiseFulfilledResult<any>).value?.success).length;
    const total = onlinePCs.length;
    const offlineCount = pcIds.length - onlinePCs.length;

    let msg = `Update triggered on ${succeeded}/${total} online PCs`;
    if (offlineCount > 0) {
      msg += ` (${offlineCount} offline — flagged for next boot)`;
    }
    showToast(msg, succeeded > 0 ? 'success' : 'error');
  };

  // Logged-in operator identity (same sessionStorage the launch flows use).
  const currentOperatorName = (): string => {
    try {
      const operatorData = sessionStorage.getItem('current_operator');
      if (!operatorData) return '';
      const operator = JSON.parse(operatorData);
      return operator.name || operator.operator_code || '';
    } catch {
      return '';
    }
  };

  // Sticky sync pause: DB flag first (durable intent, works offline), then
  // poke online agents for immediate effect (404 = old agent, flag suffices).
  const setSyncPausedOnPCs = async (pcs: PCInstance[], paused: boolean) => {
    const operatorName = currentOperatorName();
    const ok = await databaseService.setSyncPaused(pcs.map((p) => p.id), paused, operatorName || undefined);
    if (!ok) {
      showToast('Failed to write sync pause flag', 'error');
      return;
    }
    const online = pcs.filter((pc) => pc.status === 'online' && (pc.ip_address || pc.tailscale_ip));
    await Promise.allSettled(
      online.map(async (pc) => {
        const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
        return paused ? client.pauseSync(operatorName || undefined) : client.resumeSync(operatorName || undefined);
      })
    );
    showToast(
      paused
        ? `Sync paused on ${pcs.length} PC${pcs.length === 1 ? '' : 's'} (progress kept)`
        : `Sync resumed on ${pcs.length} PC${pcs.length === 1 ? '' : 's'}`,
      'success'
    );
    loadData();
  };

  const handleSetPCSyncPaused = (pc: PCInstance, paused: boolean) => setSyncPausedOnPCs([pc], paused);
  const handleSetRoomSyncPaused = (_roomId: string, pcs: PCInstance[], paused: boolean) => setSyncPausedOnPCs(pcs, paused);

  if (loading) {
    return <div className="deployment-view"><div className="deployment-loading">Loading deployment data...</div></div>;
  }

  // Get config releases for the selected configuration (for sync status panel)
  const selectedConfigReleases = selectedConfigId
    ? configReleases.filter((cr) => cr.configuration_id === selectedConfigId)
    : [];

  return (
    <div className="deployment-view">
      <div className="deployment-toolbar">
        <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#e2e8f0' }}>Deployments</span>
        <button className="deployment-btn" onClick={loadData}>Refresh</button>
        {isAdmin && (
          <button
            className="deployment-btn deployment-btn-primary"
            onClick={() => setAssignDialog({ isOpen: true })}
          >
            + Assign Release
          </button>
        )}
      </div>

      <div className="deployment-content">
        <ConfigurationMatrix
          configurations={configurations}
          configReleases={configReleases}
          rooms={allRooms}
          selectedConfigId={selectedConfigId}
          onSelectConfig={setSelectedConfigId}
          onAssignRelease={handleAssignRelease}
          onPromoteRelease={handlePromoteRelease}
          onDeleteRelease={handleDeleteRelease}
          readOnly={!isAdmin}
        />

        <RoomSyncStatus
          rooms={rooms}
          networks={networks}
          pcInstances={pcInstances}
          syncStatuses={syncStatuses}
          configReleases={selectedConfigReleases}
          onTriggerRoomUpdate={triggerRoomUpdate}
          onTriggerPCUpdate={triggerPCUpdate}
          onSetPCSyncPaused={handleSetPCSyncPaused}
          onSetRoomSyncPaused={handleSetRoomSyncPaused}
          readOnly={!isAdmin}
        />
      </div>

      <AssignReleaseDialog
        isOpen={assignDialog.isOpen}
        onClose={() => setAssignDialog({ isOpen: false })}
        onSave={handleDialogSave}
        configurations={configurations}
        experiences={experiences}
        initialConfigId={assignDialog.configId}
        initialPhase={assignDialog.phase}
        existingRelease={assignDialog.existingRelease}
      />

      <PromoteReleaseDialog
        isOpen={promoteDialog.isOpen}
        onClose={() => setPromoteDialog({ isOpen: false, existingPhases: [] })}
        onPromote={handlePromoteSave}
        release={promoteDialog.release || null}
        existingPhases={promoteDialog.existingPhases}
      />

      {toasts.map((toast) => (
        <div key={toast.id} className={`deployment-toast deployment-toast-${toast.type}`}>
          {toast.message}
        </div>
      ))}
    </div>
  );
}
